import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GruppeTier } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { GruppeTierService } from '../../../../../projects/kiene-core/src/lib/components/gruppe-tier/gruppe-tier.service';
import { EinstallenService } from '../../../../../projects/kiene-core/src/lib/components/gruppen/gruppe-einstallen/einstallen.service';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import {
    isValidOhrmarkePassScanLength,
    normalizeOhrmarkePassScanInput,
    OHRMARKE_SCAN_LENGTH,
    PASS_SCAN_LENGTH,
} from '../../../../../projects/kiene-core/src/lib/utils/ohrmarke-pass-scan.util';
import {
    emptyHerkunftFields,
    emptySammelnTier,
    parseGewichtKg,
    type SammelnGeschlecht,
    type SammelnHerkunftOption,
    type SammelnTier,
} from './dashboard-sammelstelle.types';

export type SaveSammelnResult = {
    gtId: number;
    tierlisteId: number | null;
    tier: SammelnTier;
};

function scanModeForValue(value: string): 'pass' | 'ohrmarke' | null {
    const len = normalizeOhrmarkePassScanInput(value).length;
    if (len === PASS_SCAN_LENGTH) return 'pass';
    if (len === OHRMARKE_SCAN_LENGTH) return 'ohrmarke';
    return null;
}

function recordsOf(body: unknown): any[] {
    if (Array.isArray(body)) return body;
    const records = (body as { records?: unknown })?.records;
    return Array.isArray(records) ? records : [];
}

@Injectable()
export class DashboardSammelstelleSaveService {
    constructor(
        private readonly api: KieneBackendApiService,
        private readonly gruppeTierService: GruppeTierService,
        private readonly einstallenService: EinstallenService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    lookupExisting(options: {
        ohrmarke: string;
        kundeId?: number | null;
        gruppeId?: number | null;
    }): Observable<GruppeTier | null> {
        const digits = normalizeOhrmarkePassScanInput(options.ohrmarke);
        if (!isValidOhrmarkePassScanLength(digits)) {
            return of(null);
        }
        const preferredGruppeId = Number(options.gruppeId ?? 0);
        const kundeId = Number(options.kundeId ?? 0);
        if (!(preferredGruppeId > 0) && !(kundeId > 0)) {
            return of(null);
        }
        const mode = scanModeForValue(digits);
        if (!mode) {
            return of(null);
        }
        // Search whole Betrieb when possible. Zielgruppe is only a preference among multiple hits —
        // otherwise a previously auto-selected group hides animals from other groups.
        const searchGruppeId = kundeId > 0 ? 0 : preferredGruppeId;

        return this.lookupOnce({
            digits,
            mode,
            gruppeId: searchGruppeId,
            kundeId,
            preferredGruppeId,
        }).pipe(
            switchMap(hit => {
                if (hit) {
                    return of(hit);
                }
                if (mode === 'pass' && digits.length === PASS_SCAN_LENGTH) {
                    return this.lookupOnce({
                        digits: digits.slice(-OHRMARKE_SCAN_LENGTH),
                        mode: 'ohrmarke',
                        gruppeId: searchGruppeId,
                        kundeId,
                        preferredGruppeId,
                    });
                }
                // Exact 10 failed: try last-5 Ohrmarke search (Betrieb convention: XXXXX YYYYY).
                if (mode === 'ohrmarke' && digits.length === OHRMARKE_SCAN_LENGTH) {
                    return this.lookupByLast5({
                        last5: digits.slice(-5),
                        fullDigits: digits,
                        gruppeId: searchGruppeId,
                        kundeId,
                        preferredGruppeId,
                    });
                }
                return of(null);
            }),
            switchMap(hit => {
                const gtId = Number(hit?.gt_id ?? 0);
                if (!(gtId > 0)) {
                    return of(null);
                }
                // List hit can be thin – always load full animal (bemerkungen, reklamationen, gewicht, …).
                return this.gruppeTierService.ladeTier(gtId).pipe(
                    map((full: GruppeTier | any) => {
                        const record = this.unwrapTierRecord(full) ?? full;
                        if (!Number(record?.gt_id ?? 0)) {
                            return hit;
                        }
                        // Keep date fields from the list hit when the detail payload omits/empties them.
                        return {
                            ...hit,
                            ...record,
                            einstalldatum_notnull:
                                record?.einstalldatum_notnull ?? hit?.einstalldatum_notnull ?? null,
                            kp_einstalldatum: record?.kp_einstalldatum ?? hit?.kp_einstalldatum ?? null,
                            hit_einstalldatum: record?.hit_einstalldatum ?? hit?.hit_einstalldatum ?? null,
                        } as GruppeTier;
                    }),
                    catchError(() => of(hit)),
                );
            }),
        );
    }

    /** read_one sometimes returns the row directly, sometimes wrapped. */
    private unwrapTierRecord(body: unknown): GruppeTier | null {
        if (!body || typeof body !== 'object') {
            return null;
        }
        const row = body as Record<string, unknown>;
        if (Number(row['gt_id'] ?? 0) > 0) {
            return body as GruppeTier;
        }
        const nested = row['record'] ?? row['data'] ?? row['tier'];
        if (nested && typeof nested === 'object' && Number((nested as any).gt_id ?? 0) > 0) {
            return nested as GruppeTier;
        }
        const list = recordsOf(body);
        if (list.length >= 1 && Number(list[0]?.gt_id ?? 0) > 0) {
            return list[0] as GruppeTier;
        }
        return null;
    }

    private pickBestHit(list: GruppeTier[], preferredGruppeId: number): GruppeTier | null {
        if (!list.length) {
            return null;
        }
        if (list.length === 1) {
            return list[0] ?? null;
        }
        if (preferredGruppeId > 0) {
            const inGruppe = list.find(t => Number(t.gruppe_id) === preferredGruppeId);
            if (inGruppe) {
                return inGruppe;
            }
        }
        // orderby=editiert desc already applied by API
        return list[0] ?? null;
    }

    private lookupOnce(options: {
        digits: string;
        mode: 'pass' | 'ohrmarke';
        gruppeId: number;
        kundeId: number;
        preferredGruppeId: number;
    }): Observable<GruppeTier | null> {
        let params = new HttpParams().set('tot', '0').set('orderby', 'editiert desc');
        if (options.gruppeId > 0) {
            params = params.set('gruppe_id', String(options.gruppeId));
        } else if (options.kundeId > 0) {
            params = params.set('kunde_id', String(options.kundeId));
        }
        if (options.mode === 'pass') {
            params = params.set('ohrmarke15', options.digits);
        } else {
            params = params.set('ohrmarke_kurz', options.digits);
        }
        return this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', params).pipe(
            map(body => this.pickBestHit(recordsOf(body) as GruppeTier[], options.preferredGruppeId)),
        );
    }

    /** Fallback when exact 10-digit kurz miss: last 5 digits (`ohrmarke5` → LIKE '_____YYYYY'). */
    private lookupByLast5(options: {
        last5: string;
        fullDigits: string;
        gruppeId: number;
        kundeId: number;
        preferredGruppeId: number;
    }): Observable<GruppeTier | null> {
        const last5 = String(options.last5 ?? '').replace(/\D/g, '');
        if (last5.length !== 5) {
            return of(null);
        }
        let params = new HttpParams()
            .set('tot', '0')
            .set('orderby', 'editiert desc')
            .set('ohrmarke5', last5);
        if (options.gruppeId > 0) {
            params = params.set('gruppe_id', String(options.gruppeId));
        } else if (options.kundeId > 0) {
            params = params.set('kunde_id', String(options.kundeId));
        }
        return this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_kundenportal.php', params).pipe(
            map(body => {
                const list = recordsOf(body) as GruppeTier[];
                if (!list.length) {
                    return null;
                }
                // Prefer exact full-10 match if present in last-5 results.
                const exact = list.find(
                    t =>
                        normalizeOhrmarkePassScanInput(
                            String(t.ohrmarke_kurz ?? t.ohrmarke_notnull ?? ''),
                        ) === options.fullDigits,
                );
                if (exact) {
                    return exact;
                }
                // Only auto-accept a unique last-5 hit (ambiguous otherwise).
                if (list.length === 1) {
                    return list[0] ?? null;
                }
                return this.pickBestHit(
                    list.filter(t => {
                        const kurz = normalizeOhrmarkePassScanInput(
                            String(t.ohrmarke_kurz ?? t.ohrmarke_notnull ?? ''),
                        );
                        return kurz.endsWith(last5);
                    }),
                    options.preferredGruppeId,
                );
            }),
        );
    }

    fromExisting(
        existing: GruppeTier,
        options: { ohrmarke: string; letztesAbteil: string | null },
    ): SammelnTier {
        const base = emptySammelnTier(options.ohrmarke, options.letztesAbteil);
        const herkunft = this.herkunftFromTier(existing);
        const abteil = String(existing.abteil ?? '').trim();
        const haendler = String(existing.viehhaendler ?? '').trim();
        const haendlerId = Number(existing.viehhaendler_id ?? 0);
        const bemerkungen = (existing.bemerkungen ?? [])
            .map((b: unknown) => {
                if (typeof b === 'string') return b.trim();
                return String((b as { bemerkung?: string })?.bemerkung ?? '').trim();
            })
            .filter(Boolean);
        const reklamationen: string[] = [];
        const reklamationIds: number[] = [];
        for (const r of existing.reklamationen ?? []) {
            const label = String(
                (r as { reklamation?: string; bezeichnung?: string })?.reklamation ??
                    (r as { bezeichnung?: string })?.bezeichnung ??
                    '',
            ).trim();
            const id = Number((r as { tierreklamation_id?: number })?.tierreklamation_id ?? 0);
            if (label && !reklamationen.includes(label)) reklamationen.push(label);
            if (id > 0 && !reklamationIds.includes(id)) reklamationIds.push(id);
        }
        const gtId = Number(existing.gt_id);
        const sourceGruppeId = Number(existing.gruppe_id);
        return {
            ...base,
            geschlecht: this.geschlechtFromTier(existing),
            gewicht: this.formatGewicht(existing.einstallgewicht),
            haendler: haendler || null,
            haendlerId: haendlerId > 0 ? haendlerId : null,
            herkunft: herkunft?.label ?? null,
            herkunftKundeId: herkunft?.herkunftKundeId ?? null,
            herkunftFields: herkunft?.fields ?? null,
            abteil: abteil || options.letztesAbteil,
            bemerkungen,
            reklamationen,
            reklamationIds,
            gtId: gtId > 0 ? gtId : null,
            sourceGruppeId: sourceGruppeId > 0 ? sourceGruppeId : null,
            baselineBemerkungen: [...bemerkungen],
        };
    }

    herkunftFromTier(tier: GruppeTier): SammelnHerkunftOption | null {
        const betrieb = String(tier.herkunft_betrieb ?? '').trim();
        const vvvo = String(tier.herkunft_vvvo ?? '').trim();
        if (!betrieb && !vvvo) return null;
        const kundeId = Number(tier.herkunft_kunde_id ?? 0);
        return {
            label: betrieb || vvvo,
            herkunftKundeId: kundeId > 0 ? kundeId : null,
            fields: {
                herkunft_vvvo: vvvo,
                herkunft_betrieb: betrieb,
                herkunft_strasse: String(tier.herkunft_strasse ?? '').trim(),
                herkunft_plz: String(tier.herkunft_plz ?? '').trim(),
                herkunft_ort: String(tier.herkunft_ort ?? '').trim(),
                herkunft_ortsteil: String(tier.herkunft_ortsteil ?? '').trim(),
            },
        };
    }

    herkunftOptionFromGroupRow(row: {
        herkunft_kunde_id?: number;
        herkunft_betrieb?: string;
        herkunft_vvvo?: string | null;
    }): SammelnHerkunftOption | null {
        const label = String(row.herkunft_betrieb ?? '').trim();
        if (!label) return null;
        const kundeId = Number(row.herkunft_kunde_id ?? 0);
        return {
            label,
            herkunftKundeId: kundeId > 0 ? kundeId : null,
            fields: {
                ...emptyHerkunftFields(),
                herkunft_betrieb: label,
                herkunft_vvvo: String(row.herkunft_vvvo ?? '').trim(),
            },
        };
    }

    private geschlechtFromTier(tier: GruppeTier): SammelnGeschlecht | null {
        const id = Number(tier.geschlecht_id ?? 0);
        if (id === 1) return 'm';
        if (id === 2) return 'w';
        const raw = String(tier.geschlecht ?? '').trim().toLowerCase();
        if (raw === 'm' || raw.startsWith('männ') || raw.startsWith('maenn')) return 'm';
        if (raw === 'w' || raw.startsWith('weib')) return 'w';
        return null;
    }

    private formatGewicht(value: unknown): string {
        if (value == null || value === '') return '';
        const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
        if (!Number.isFinite(n) || n <= 0) return '';
        return String(n).replace('.', ',');
    }

    save(options: {
        tier: SammelnTier;
        gruppeId?: number | null;
        kundeId?: number | null;
        tierlisteId?: number | null;
        datum: string;
    }): Observable<SaveSammelnResult> {
        if (Number(options.tier.gtId ?? 0) > 0) {
            return this.updateExisting(options.tier);
        }
        return this.createNew(options);
    }

    /**
     * Create unknown Sammelstelle animal on an Einstall-Tierliste and apply draft fields
     * (Gewicht, Abteil, Händler, Herkunft, Bemerkung, Reklamation, Geschlecht).
     */
    createOnEinstallListe(options: {
        tier: SammelnTier;
        gruppeId: number;
        kundeId?: number | null;
        tierlisteId: number;
        datum: string;
    }): Observable<SaveSammelnResult> {
        return this.createNew({
            tier: options.tier,
            gruppeId: options.gruppeId,
            kundeId: options.kundeId,
            tierlisteId: options.tierlisteId,
            datum: options.datum,
        });
    }

    private updateExisting(tier: SammelnTier): Observable<SaveSammelnResult> {
        const gtId = Number(tier.gtId ?? 0);
        if (!(gtId > 0)) {
            return throwError(() => new Error('Kein bestehendes Tier zum Aktualisieren.'));
        }
        const gruppeId = Number(tier.sourceGruppeId ?? 0);
        const ensureGruppe$ =
            gruppeId > 0
                ? of(gruppeId)
                : this.gruppeTierService.ladeTier(gtId).pipe(map((t: GruppeTier) => Number(t?.gruppe_id ?? 0)));
        return ensureGruppe$.pipe(
            switchMap(gid => {
                if (!(gid > 0)) {
                    return throwError(() => new Error('Gruppe des Tieres konnte nicht ermittelt werden.'));
                }
                return this.applyFieldUpdates(gtId, gid, tier).pipe(
                    map(() => ({
                        gtId,
                        tierlisteId: null,
                        tier: { ...tier, gtId, sourceGruppeId: gid, saved: true },
                    })),
                );
            }),
        );
    }

    private createNew(options: {
        tier: SammelnTier;
        gruppeId?: number | null;
        kundeId?: number | null;
        tierlisteId?: number | null;
        datum: string;
    }): Observable<SaveSammelnResult> {
        const gruppeId = Number(options.gruppeId ?? 0);
        if (!(gruppeId > 0)) {
            return throwError(() => new Error('Bitte zuerst eine Zielgruppe wählen.'));
        }
        const mode = scanModeForValue(options.tier.ohrmarke) ?? 'ohrmarke';
        const firstBemerkung = options.tier.bemerkungen[0]?.trim() || undefined;
        const geschlechtId =
            options.tier.geschlecht === 'm' ? 1 : options.tier.geschlecht === 'w' ? 2 : undefined;
        const ensureListe$ =
            Number(options.tierlisteId ?? 0) > 0
                ? of(Number(options.tierlisteId))
                : this.einstallenService
                      .createEinstallTierliste(gruppeId, options.kundeId)
                      .pipe(map(res => Number(res?.tierliste_id || 0)));
        return ensureListe$.pipe(
            switchMap(tierlisteId => {
                if (!(tierlisteId > 0)) {
                    return throwError(() => new Error('Einstall-Tierliste konnte nicht erstellt werden.'));
                }
                const payload: Record<string, unknown> = {
                    gruppe_id: gruppeId,
                    ohrmarke: options.tier.ohrmarke,
                    kp_einstalldatum: options.datum,
                    einstall_tierliste_id: tierlisteId,
                    pass_gescannt: mode === 'pass' ? 1 : 0,
                };
                if (options.kundeId && Number(options.kundeId) > 0) {
                    payload.kunde_id = Number(options.kundeId);
                }
                if (options.tier.abteil?.trim()) payload.abteil = options.tier.abteil.trim();
                if (firstBemerkung) payload.einstall_bemerkung = firstBemerkung;
                if (geschlechtId) payload.geschlecht_id = geschlechtId;
                return this.api.post(this.apiBaseUrl + 'gruppe_tier/create_kundenportal.php', payload, undefined).pipe(
                    switchMap((created: GruppeTier) => {
                        const gtId = Number(created?.gt_id ?? 0);
                        if (!(gtId > 0)) {
                            return throwError(() => new Error('Tier konnte nicht gespeichert werden.'));
                        }
                        return this.applyFieldUpdates(gtId, gruppeId, options.tier, {
                            skipFirstBemerkung: Boolean(firstBemerkung),
                        }).pipe(
                            map(() => ({
                                gtId,
                                tierlisteId,
                                tier: { ...options.tier, gtId, sourceGruppeId: gruppeId, saved: true },
                            })),
                        );
                    }),
                );
            }),
        );
    }

    private applyFieldUpdates(
        gtId: number,
        gruppeId: number,
        tier: SammelnTier,
        opts?: { skipFirstBemerkung?: boolean },
    ): Observable<boolean> {
        const abteil = tier.abteil?.trim() || '';
        const abteil$ = abteil
            ? this.api.post(this.apiBaseUrl + 'gruppe_tier/update_abteil.php', { abteil, tiere: [{ gt_id: gtId }] }, null)
            : of(null);
        const herkunft = tier.herkunftFields;
        const hasHerkunft =
            herkunft &&
            (String(herkunft.herkunft_betrieb ?? '').trim() || String(herkunft.herkunft_vvvo ?? '').trim());
        const herkunft$ = hasHerkunft
            ? this.gruppeTierService.updateHerkunft({ gt_id: gtId, ...herkunft } as GruppeTier)
            : of(null);
        const haendler$ =
            tier.haendlerId != null && tier.haendlerId > 0
                ? this.gruppeTierService.updateHaendler({
                      gt_id: gtId,
                      viehhaendler_id: tier.haendlerId,
                      betriebskontakt_haendler_id: null,
                  } as GruppeTier)
                : of(null);
        const gewicht = parseGewichtKg(tier.gewicht);
        const gewicht$ =
            gewicht != null && gruppeId > 0
                ? this.gruppeTierService.updateEinstallgewichteMultiple(
                      gruppeId,
                      [{ gt_id: gtId } as GruppeTier],
                      gewicht,
                  )
                : of(null);
        const geschlechtId = tier.geschlecht === 'm' ? 1 : tier.geschlecht === 'w' ? 2 : null;
        const geschlecht$ =
            geschlechtId != null
                ? this.gruppeTierService.ladeTier(gtId).pipe(
                      switchMap((current: GruppeTier) => {
                          if (Number(current?.geschlecht_id) === geschlechtId) return of(null);
                          return this.gruppeTierService.update({
                              ...current,
                              gt_id: gtId,
                              geschlecht_id: geschlechtId,
                          } as GruppeTier);
                      }),
                  )
                : of(null);
        const baseline = new Set((tier.baselineBemerkungen ?? []).map(b => b.trim()).filter(Boolean));
        let bemerkungen = tier.bemerkungen.map(b => b.trim()).filter(Boolean);
        if (opts?.skipFirstBemerkung && bemerkungen.length > 0) bemerkungen = bemerkungen.slice(1);
        const newBemerkungen = bemerkungen.filter(b => !baseline.has(b));
        const bemerkungChain = newBemerkungen.reduce(
            (acc, bemerkung) =>
                acc.pipe(
                    switchMap(() =>
                        this.api.post(
                            this.apiBaseUrl + 'gruppe_tier_bemerkung/update_kundenportal_bemerkungen.php',
                            { gt_ids: [gtId], bemerkung },
                            null,
                        ),
                    ),
                ),
            of(null) as Observable<unknown>,
        );
        const reklamationIds = (tier.reklamationIds ?? []).filter(id => id > 0);
        const reklamation$ =
            reklamationIds.length > 0 && gruppeId > 0
                ? this.gruppeTierService.updateReklamationenMultiple(
                      [{ gt_id: gtId } as GruppeTier],
                      reklamationIds,
                      gruppeId,
                  )
                : of(null);
        return abteil$.pipe(
            switchMap(() => geschlecht$),
            switchMap(() => herkunft$),
            switchMap(() => haendler$),
            switchMap(() => gewicht$),
            switchMap(() => bemerkungChain),
            switchMap(() => reklamation$),
            map(() => true),
        );
    }
}
